import React, { useEffect, useRef } from "react";
import { Container, Graphics } from "@pixi/react";
import { useSelector } from "react-redux";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import anime from "animejs";
import LoaderController from "./LoaderController";

const Loader = () => {
  const { dimensions, uiSymbolWidth, uiSymbolHeight, device, orientation } =
    useDimensions();
  const loadingProgress = useSelector((state) => state.loadingProgress);
  const containerRef = useRef(null);
  const loaderControllerRef = useRef(null);
  const mountTimeRef = useRef(null);
  const fadeOutTimeoutRef = useRef(null);
  const MIN_DISPLAY_TIME = 3000; // 3 seconds minimu m

  // Track mount time
  useEffect(() => {
    mountTimeRef.current = Date.now();
  }, []);

  // Initialize spine controller on mount (preloader loads its own assets)
  useEffect(() => {
    if (
      containerRef.current &&
      !loaderControllerRef.current &&
      dimensions &&
      uiSymbolWidth &&
      uiSymbolHeight
    ) {
      loaderControllerRef.current = new LoaderController(
        containerRef.current,
        dimensions,
        uiSymbolWidth,
        uiSymbolHeight,
        { device, orientation }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update spine when dimensions, symbol sizes, or device/orientation changes
  useEffect(() => {
    if (loaderControllerRef.current) {
      loaderControllerRef.current.updateDimensions(
        dimensions,
        uiSymbolWidth,
        uiSymbolHeight,
        { device, orientation }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions, uiSymbolWidth, uiSymbolHeight, device, orientation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
        fadeOutTimeoutRef.current = null;
      }
      if (loaderControllerRef.current) {
        loaderControllerRef.current.cleanup();
        loaderControllerRef.current = null;
      }
    };
  }, []);

  // Set initial alpha
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.alpha = 1;
    }
  }, []);

  // Fade out and cleanup when loading completes (with minimum display time)
  useEffect(() => {
    if (loadingProgress.status === "success" && containerRef.current) {
      // Clear any existing timeout
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
        fadeOutTimeoutRef.current = null;
      }

      const elapsedTime = Date.now() - (mountTimeRef.current || Date.now());
      const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsedTime);

      const performFadeOut = () => {
        if (loaderControllerRef.current) {
          loaderControllerRef.current.cleanup();
          loaderControllerRef.current = null;
        }

        const animation = anime.timeline();
        animation.add({
          targets: containerRef.current,
          alpha: 1,
          duration: 300,
        });
        animation.add({
          targets: containerRef.current,
          alpha: [1, 0],
          duration: 300,
          easing: "easeOutQuad",
        });
      };

      if (remainingTime > 0) {
        // Wait for remaining time before fading out
        fadeOutTimeoutRef.current = setTimeout(() => {
          fadeOutTimeoutRef.current = null;
          performFadeOut();
        }, remainingTime);
      } else {
        // Already past minimum time, fade out immediately
        performFadeOut();
      }
    }

    // Cleanup function to clear timeout if effect re-runs or component unmounts
    return () => {
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
        fadeOutTimeoutRef.current = null;
      }
    };
  }, [loadingProgress.status]);

  return (
    <Container ref={containerRef} name="secondary_loader" zIndex={10000}>
      <Graphics
        name="secondary_loader_background"
        draw={(g) => {
          g.clear();
          g.beginFill(0x000000, 1);
          g.drawRect(0, 0, dimensions.width, dimensions.height);
          g.endFill();
        }}
        eventMode="none"
      />
      {/* Logo is managed by PopiLoaderController */}
    </Container>
  );
};

export default Loader;
